import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';
import { cards } from '../assets/cards';
import {
	HTV2DownloadMobileAppCTA,
	HTV2AddProfilePictureCTA,
	HTV2ChangeSiteThemeCTA,
} from './components/HTV2ActionCardLinkCTA';
import { HTV2ExplorePremiumCTA } from './components/HTV2ExplorePremiumCTA';
import { HTV2CreatePageCTA, HTV2CreateWhiteboardCTA } from './components/HTV2CreateContentCTA';
import { HTV2CreateSpaceCTA } from './components/HTV2CreateSpaceCTA';
import {
	commonCardI18n,
	RenderedFormattedMessageCreateCTA,
	RenderedFormattedMessageCreateTitle,
} from './HTV2CommonCardsI18n';
import type { OnboardingAction, OnboardingActionStates } from './HTV2CardTypes';

const i18n = defineMessages({
	addProfilePictureTitle: {
		id: 'experiment-home-onboarding-takeover-v2.add-profile-picture.title',
		defaultMessage: 'Add your profile photo',
		description:
			'This will be shown as a title on a card embedded into the home screen as a part of onboarding.',
	},
	addProfilePictureDescription: {
		id: 'experiment-home-onboarding-takeover-v2.add-profile-picture.description',
		defaultMessage: 'Welcome your teammates to Confluence with a familiar face.',
		description:
			'This will be shown as a description on a card embedded into the home screen as a part of onboarding.',
	},
	addProfilePictureCTA: {
		id: 'experiment-home-onboarding-takeover-v2.add-profile-picture.cta',
		defaultMessage: 'Add photo',
		description:
			'This will be shown on a button embedded into the home screen as a part of onboarding. Clicking the button will open a new settings page where they can add a profile photo',
	},
	changeSiteThemeTitle: {
		id: 'experiment-home-onboarding-takeover-v2.change-site-theme.title',
		defaultMessage: 'Add your company’s branding',
		description:
			'This will be shown as a title on a card embedded into the home screen as a part of onboarding.',
	},
	changeSiteThemeDescription: {
		id: 'experiment-home-onboarding-takeover-v2.change-site-theme.description',
		defaultMessage: 'Give Confluence a familiar look by choosing colors and adding your logo.',
		description:
			'This will be shown as a description on a card embedded into the home screen as a part of onboarding.',
	},
	changeSiteThemeCTA: {
		id: 'experiment-home-onboarding-takeover-v2.change-site-theme.cta',
		defaultMessage: 'Add branding',
		description:
			'This will be shown on a button embedded into the home screen as a part of onboarding. Clicking the button will open a new adminhub page where they change some site color themes and logos',
	},
	explorePremiumTitle: {
		id: 'experiment-home-onboarding-takeover-v2.explore-premium.title',
		defaultMessage: 'Try Premium features',
		description:
			'This will be shown as a title on a card embedded into the home screen as a part of onboarding.',
	},
	explorePremiumDescription: {
		id: 'experiment-home-onboarding-takeover-v2.explore-premium.description',
		defaultMessage:
			'You’re on a {trialDaysLeft}-day free trial of Confluence Premium. See what’s included and take it out for a spin.',
		description:
			'This will be shown as a description on a card embedded into the home screen as a part of onboarding.',
	},
	explorePremiumCTA: {
		id: 'experiment-home-onboarding-takeover-v2.explore-premium.cta',
		defaultMessage: 'See Premium features',
		description:
			'This will be shown on a button embedded into the home screen as a part of onboarding. Clicking the button open a modal that teaches them about premium features',
	},
	downloadMobileAppTitle: {
		id: 'experiment-home-onboarding-takeover-v2.download-mobile-app.title',
		defaultMessage: 'Take Confluence on the go',
		description:
			'This will be shown as a title on a card embedded into the home screen as a part of onboarding.',
	},
	downloadMobileAppDescription: {
		id: 'experiment-home-onboarding-takeover-v2.download-mobile-app.description',
		defaultMessage: 'Collaborate with your team and sync work across all of your devices.',
		description:
			'This will be shown as a description on a card embedded into the home screen as a part of onboarding.',
	},
	downloadMobileAppCTA: {
		id: 'experiment-home-onboarding-takeover-v2.download-mobile-app.cta',
		defaultMessage: 'Learn about the app',
		description:
			'This will be shown on a button embedded into the home screen as a part of onboarding. Clicking the button will open a new page where they can download the mobile app',
	},
});

export const LAND_ONBOARDING_ACTIONS_DATA = [
	{
		id: 'createPage',
		title: <RenderedFormattedMessageCreateTitle />,
		description: commonCardI18n.createPageDescription,
		image: cards.createPage,
		cta: ({ onClick }: { onClick: () => void }) => (
			<HTV2CreatePageCTA onClick={onClick}>
				<RenderedFormattedMessageCreateCTA />
			</HTV2CreatePageCTA>
		),
	},
	{
		id: 'createSpace',
		title: <FormattedMessage {...commonCardI18n.createSpaceTitle} />,
		description: commonCardI18n.createSpaceDescription,
		image: cards.createSpace,
		cta: ({ onClick }: { onClick: () => void }) => <HTV2CreateSpaceCTA onClick={onClick} />,
	},
	{
		id: 'createWhiteboard',
		title: <FormattedMessage {...commonCardI18n.createWhiteboardTitle} />,
		description: commonCardI18n.createWhiteboardDescription,
		image: cards.createWhiteboard,
		cta: ({ onClick }: { onClick: () => void }) => (
			<HTV2CreateWhiteboardCTA onClick={onClick}>
				<FormattedMessage {...commonCardI18n.createWhiteboardCTA} />
			</HTV2CreateWhiteboardCTA>
		),
	},
	{
		id: 'addProfilePicture',
		title: <FormattedMessage {...i18n.addProfilePictureTitle} />,
		description: i18n.addProfilePictureDescription,
		image: cards.addProfilePicture,
		cta: ({ onClick }: { onClick: () => void }) => (
			<HTV2AddProfilePictureCTA onClick={onClick}>
				<FormattedMessage {...i18n.addProfilePictureCTA} />
			</HTV2AddProfilePictureCTA>
		),
	},
	{
		id: 'changeSiteTheme',
		title: <FormattedMessage {...i18n.changeSiteThemeTitle} />,
		description: i18n.changeSiteThemeDescription,
		image: cards.changeSiteTheme,
		cta: ({ onClick }: { onClick: () => void }) => (
			<HTV2ChangeSiteThemeCTA onClick={onClick}>
				<FormattedMessage {...i18n.changeSiteThemeCTA} />
			</HTV2ChangeSiteThemeCTA>
		),
	},
	{
		id: 'explorePremium',
		title: <FormattedMessage {...i18n.explorePremiumTitle} />,
		description: i18n.explorePremiumDescription,
		image: cards.explorePremium,
		cta: ({ onClick }: { onClick: () => void }) => (
			<HTV2ExplorePremiumCTA isDisabled onClick={onClick}>
				<FormattedMessage {...i18n.explorePremiumCTA} />
			</HTV2ExplorePremiumCTA>
		),
	},
	{
		id: 'downloadMobileApp',
		title: <FormattedMessage {...i18n.downloadMobileAppTitle} />,
		description: i18n.downloadMobileAppDescription,
		image: cards.downloadMobileApp,
		cta: ({ onClick }: { onClick: () => void }) => (
			<HTV2DownloadMobileAppCTA onClick={onClick}>
				<FormattedMessage {...i18n.downloadMobileAppCTA} />
			</HTV2DownloadMobileAppCTA>
		),
	},
] as const;

export const transformLandOnboardingActions = ({
	formattedTrialDaysLeft,
	isPremiumCardEligible,
	onboardingActionStates,
}: {
	formattedTrialDaysLeft: number;
	isPremiumCardEligible: boolean;
	onboardingActionStates: OnboardingActionStates;
}) => {
	return LAND_ONBOARDING_ACTIONS_DATA.map((action) => ({
		...action,
		state: onboardingActionStates[action.id] || 'pending',
		//  Updates the trial days left description of the explore premium card
		description:
			action.id === 'explorePremium'
				? { ...action.description, values: { trialDaysLeft: formattedTrialDaysLeft } }
				: action.description,
	})).reduce((actions: OnboardingAction[], currentAction: OnboardingAction) => {
		if (currentAction.id === 'explorePremium') {
			if (isPremiumCardEligible) {
				return [
					// Updates the CTA of the explore premium card
					...actions,
					{
						...currentAction,
						cta: ({ onClick }: { onClick: () => void }) => (
							<HTV2ExplorePremiumCTA
								onClick={onClick}
								formattedTrialDaysLeft={formattedTrialDaysLeft}
							>
								<FormattedMessage {...i18n.explorePremiumCTA} />
							</HTV2ExplorePremiumCTA>
						),
					},
				];
			} else {
				return actions; // Removes the explore premium card
			}
		}
		return [...actions, currentAction]; // Adds the rest of the cards
	}, []);
};
